/**
 * InfinitSales - Widgets do site: moldura e pecas comuns dos modais
 * (codigo, criar, editar).
 *
 * Mesmo jeito dos modais de conexao do Instagram e do Messenger: fundo
 * escurecido, cartao de raio 16, fechar redondo no canto, botoes de raio 10.
 * O cartao e uma coluna (cabecalho, abas, corpo com rolagem, rodape fixo)
 * para o botao principal nao sumir quando o formulario cresce. No celular o
 * cartao ocupa a largura toda com 16 px de margem (padding do fundo).
 * Cores so do tema: verde da marca no botao primario, na aba ativa e no foco.
 */
import React, { useEffect, useId, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Respiro lateral: 24 px no desktop, 16 px no celular estreito.
export const PAD_X = 'clamp(16px, 5vw, 24px)';
export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** Mensagem do backend (detail texto ou {message}) ou a padrao. */
export const errorMessage = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return (typeof d === 'object' ? d?.message : d) || fallback;
};

/**
 * Copia para a area de transferencia. Sem a API (http, navegador antigo),
 * seleciona o texto de `el` e tenta o execCommand; se falhar, o texto fica
 * selecionado para o Ctrl+C.
 */
export const copyText = async (text, el) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* cai na selecao */ }
  if (!el) return false;
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    return document.execCommand('copy');
  } catch {
    return false;
  }
};

export const formStyles = (colors) => {
  const danger = colors.danger || '#ef4444';
  const input = {
    width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14, lineHeight: 1.4,
    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
    color: colors.textPrimary, outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const btn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  };
  return {
    field: { marginBottom: 16 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 },
    optional: { fontWeight: 400, color: colors.textSecondary },
    hint: { fontSize: 12, lineHeight: 1.4, color: colors.textSecondary, marginTop: 6 },
    input,
    textarea: { ...input, minHeight: 76, resize: 'vertical' },
    primaryBtn: {
      ...btn, width: '100%', border: `1px solid ${colors.accentPrimary}`,
      backgroundColor: colors.accentPrimary, color: '#fff',
    },
    secondaryBtn: {
      ...btn, fontWeight: 500, border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgTertiary, color: colors.textPrimary,
    },
    dangerBtn: { ...btn, border: `1px solid ${danger}`, backgroundColor: 'transparent', color: danger },
  };
};

/**
 * Foco no verde da marca (o CSS global tira o outline dos campos), o spin e,
 * abaixo de 640 px, modal na largura toda (a margem de 16 px e do fundo).
 */
export const WidgetUiStyles = ({ colors }) => (
  <style>{`
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .wdg-field:focus { border-color: ${colors.accentPrimary} !important; box-shadow: 0 0 0 3px ${colors.accentPrimary}33; }
    .wdg-btn:focus-visible { outline: 2px solid ${colors.accentPrimary}; outline-offset: 2px; }
    .wdg-btn:disabled { opacity: 0.6; cursor: not-allowed !important; }
    @media (max-width: 639px) { .wdg-modal { max-width: none !important; } }
  `}</style>
);

export const ErrorBox = ({ children }) => {
  const { colors } = useTheme();
  const danger = colors.danger || '#ef4444';
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', marginBottom: 12,
      borderRadius: 12, backgroundColor: `${danger}15`, border: `1px solid ${danger}30`,
    }}>
      <AlertTriangle size={16} color={danger} style={{ flexShrink: 0, marginTop: 2 }} />
      <p style={{ margin: 0, flex: 1, fontSize: 13, lineHeight: 1.45, color: danger, overflowWrap: 'anywhere' }}>
        {children}
      </p>
    </div>
  );
};

/**
 * Moldura. Com `onSubmit` o cartao vira <form> (Enter envia e o botao do
 * rodape pode ser type="submit"). `tabs` fica fixo entre o cabecalho e o
 * corpo; `footer` fica fixo embaixo. `bodyMinHeight` segura a altura do corpo
 * (abas de tamanhos diferentes nao fazem a janela pular), limitada pela tela.
 */
const WidgetModalShell = ({
  title, subtitle, onClose, onSubmit, maxWidth = 460, tabs = null, footer = null, bodyMinHeight = null, children,
}) => {
  const { colors, theme } = useTheme();
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // So fecha no clique que COMECA no fundo (arrastar uma selecao para fora nao fecha).
  const downOnOverlay = useRef(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const Card = onSubmit ? 'form' : 'div';
  const dark = theme === 'dark';

  return (
    <div
      onMouseDown={(e) => { downOnOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOnOverlay.current && e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, padding: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Card
        className="wdg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={onSubmit}
        noValidate={onSubmit ? true : undefined}
        style={{
          position: 'relative', width: '100%', maxWidth, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          backgroundColor: colors.bgPrimary, color: colors.textPrimary, borderRadius: 16,
          border: dark ? `1px solid ${colors.borderColor}` : 'none',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          colorScheme: dark ? 'dark' : 'light',
        }}
      >
        <button
          type="button"
          className="wdg-btn"
          aria-label="Fechar"
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, zIndex: 1, width: 36, height: 36,
            borderRadius: '50%', border: 'none', backgroundColor: colors.bgTertiary,
            color: colors.textSecondary, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={20} />
        </button>

        <div style={{ flexShrink: 0, padding: `24px 64px 0 ${PAD_X}` }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: 1.3, color: colors.textPrimary }}>
            {title}
          </h2>
          {subtitle && (
            <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.45, color: colors.textSecondary }}>{subtitle}</p>
          )}
        </div>

        {tabs}

        <div style={{
          flex: 1, minHeight: bodyMinHeight ? `min(${bodyMinHeight}px, 55vh)` : 0, overflowY: 'auto', overflowX: 'hidden',
          padding: `20px ${PAD_X} ${footer ? '12px' : '24px'}`,
        }}>
          {children}
        </div>

        {footer && <div style={{ flexShrink: 0, padding: `4px ${PAD_X} 24px` }}>{footer}</div>}
      </Card>
      <WidgetUiStyles colors={colors} />
    </div>
  );
};

export default WidgetModalShell;
